import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError, fileUrl } from "../api";
import { byProductName } from "../lib/utils";
import { BrutalButton, BrutalCard, BrutalInput, StockBadge } from "../components/Brutal";
import { Plus, Trash, PencilSimple, MagnifyingGlass, PaperPlaneTilt, Eye, FileCsv, Factory, SprayBottle, ListChecks, CurrencyDollar, ArrowsMerge, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import AssignProductDialog from "../components/AssignProductDialog";
import { BulkGiveDialog } from "../components/BulkGiveDialog";
import SaleRequestDialog from "../components/SaleRequestDialog";
import ProductImports from "../components/ProductImports";
import { useAuth } from "../AuthContext";
import { isEliteConvertible } from "../components/NasalTiers";
import { ProductTitle } from "../components/ProductTitle";

export const PRODUCT_CATEGORIES = ["Injectable", "Precision Nasal Mist", "Topical", "Flagship Topical", "supplies",
  "Mushroom Coffee", "Adaptogens", "Protein", "Supplements"];

// v414.51 safety net: a product whose category isn't EXACTLY one of the
// canonical values used to render in NO section at all (invisible even under
// "All"). Match case-insensitively; truly unknown values land in "Other".
const CAT_LOOKUP = Object.fromEntries(PRODUCT_CATEGORIES.map((c) => [c.toLowerCase(), c]));
export const effectiveCategory = (p) => {
  const c = (p.category || "").trim();
  if (!c) return "Injectable";
  return CAT_LOOKUP[c.toLowerCase()] || "Other";
};

export default function InventoryPage() {
  const { t } = useTranslation();
  const { isOwner, isSuperOwner, user } = useAuth();
  // v400.35 — HARD UNLOCK: master inventory management (± stock, edit, give)
  // is available to any caller in SUPER_OWNER_EMAILS (Moin + Harvey + Sukhinder).
  // Create/import/delete master products stay Moin-only.
  const canManage = isOwner || isSuperOwner;
  // v404.8 Moin-parity: super-owners can also create/import/delete master products.
  const canCreate = isOwner || isSuperOwner;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [merging, setMerging] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [selling, setSelling] = useState(null);
  const [showSaleRequest, setShowSaleRequest] = useState(false);
  const [showPartnerSale, setShowPartnerSale] = useState(false);
  const [showBulkGive, setShowBulkGive] = useState(false); // v405.10 Bulk Give
  // v406.7 — email deep link: /inventory?give={distId}&product_name=…&qty=…
  // opens the Bulk Give dialog pre-filled (from the restock-request alert).
  const [searchParams, setSearchParams] = useSearchParams();
  const [bulkGiveInitial, setBulkGiveInitial] = useState(null);
  useEffect(() => {
    const give = searchParams.get("give");
    if (give) {
      setBulkGiveInitial({
        distId: give,
        productName: searchParams.get("product_name") || "",
        qty: searchParams.get("qty") || "",
      });
      setShowBulkGive(true);
      setSearchParams({}, { replace: true }); // strip so refresh doesn't reopen
    }
    // eslint-disable-next-line
  }, []);
  const [directSaleFor, setDirectSaleFor] = useState(null); // v401.7 per-card $ Direct Sale
  const [producing, setProducing] = useState(null);
  const [converting, setConverting] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importsKey, setImportsKey] = useState(0);  // bump after every CSV import + undo to refresh panel
  const csvInputRef = React.useRef(null);

  const onPickCsv = () => csvInputRef.current?.click();
  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset so re-selecting same file fires again
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      if (data.imported > 0) {
        toast.success(t("inventory.csv_imported", { count: data.imported }));
        setImportsKey((k) => k + 1);
      }
      if (data.failed > 0) {
        const firstFew = (data.errors || []).slice(0, 3).map((er) => `Row ${er.row}: ${er.error}`).join("\n");
        toast.error(t("inventory.csv_failed", { count: data.failed }), {
          description: firstFew,
          duration: 8000,
        });
      }
      if (data.imported === 0 && data.failed === 0) {
        toast.message(t("inventory.csv_empty"));
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImporting(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/products");
      setProducts(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm(t("inventory.soft_delete_confirm"))) return;
    try {
      await api.delete(`/products/${id}`);
      setProducts((p) => p.filter((x) => x.id !== id));
      toast.success(t("inventory.moved_to_trash"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // v401.5: all product lists sort ALPHABETICALLY by name.
  const filtered = products
    .filter(
      (p) =>
        p.name.toLowerCase().includes(search.toLowerCase()) &&
        (catFilter === "all" || effectiveCategory(p) === catFilter)
    )
    .sort(byProductName);
  const hasOther = products.some((p) => effectiveCategory(p) === "Other");
  const grouped = [...PRODUCT_CATEGORIES, "Other"]
    .map((cat) => [cat, filtered.filter((p) => effectiveCategory(p) === cat)])
    .filter(([, list]) => list.length > 0);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{t("inventory.title")}</h1>
          <p className="text-sm sm:text-base text-zinc-600 mt-1">
            {canManage ? t("inventory.subtitle") : t("inventory.subtitle_readonly")}
          </p>
        </div>
        {canCreate && (
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Link to="/stock-count">
              <BrutalButton variant="secondary" data-testid="stock-count-link">
                <ListChecks size={16} weight="bold" /> {t("stock_count.title")}
              </BrutalButton>
            </Link>
            <Link to="/inventory/new">
              <BrutalButton data-testid="add-product-button">
                <Plus size={16} weight="bold" /> {t("dashboard.add_product")}
              </BrutalButton>
            </Link>
            <BrutalButton
              variant="secondary"
              onClick={() => setShowBulkGive(true)}
              data-testid="bulk-give-button"
              className="!border-[#c9a24a] !text-[#8a6a1c] hover:!bg-[#fbf6e7]"
            >
              <PaperPlaneTilt size={16} weight="bold" /> {t("bulk_give.button")}
            </BrutalButton>
            <BrutalButton
              variant="secondary"
              onClick={() => setShowSaleRequest(true)}
              data-testid="master-sale-request-btn"
              className="!border-emerald-600 !text-emerald-700 hover:!bg-emerald-50"
            >
              <CurrencyDollar size={16} weight="bold" /> {t("sale_request.new_btn")}
            </BrutalButton>
            <BrutalButton
              variant="secondary"
              onClick={onPickCsv}
              disabled={importing}
              data-testid="import-csv-button"
            >
              {importing ? (
                <span className="inline-block w-4 h-4 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
              ) : (
                <FileCsv size={16} weight="bold" />
              )}
              {importing ? t("inventory.csv_importing") : t("inventory.import_csv")}
            </BrutalButton>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleCsvImport}
              data-testid="csv-import-input"
            />
            <Link to="/inventory/intake">
              <BrutalButton variant="secondary" data-testid="intake-cta-button">
                {t("dashboard.scan_csv_intake")}
              </BrutalButton>
            </Link>
          </div>
        )}
        {!canManage && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-zinc-100 border border-zinc-200 text-xs font-medium text-zinc-600 self-start" data-testid="readonly-badge">
            <Eye size={14} weight="bold" /> {t("inventory.read_only")}
          </span>
        )}
        {/* v401.5 Distributor POS — partners sell their OWN consigned stock */}
        {user?.role === "partner" && (
          <BrutalButton
            variant="secondary"
            onClick={() => setShowPartnerSale(true)}
            data-testid="partner-sale-request-btn"
            className="!border-emerald-600 !text-emerald-700 hover:!bg-emerald-50 self-start"
          >
            <CurrencyDollar size={16} weight="bold" /> {t("sale_request.new_btn")}
          </BrutalButton>
        )}
      </div>

      {canCreate && (
        <ProductImports
          refreshKey={importsKey}
          onAfterUndo={() => { setImportsKey((k) => k + 1); load(); }}
        />
      )}

      <div className="relative">
        <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2" weight="bold" />
        <BrutalInput
          className="pl-12"
          placeholder={t("inventory.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="inventory-search-input"
        />
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="category-filter-row">
        {["all", ...PRODUCT_CATEGORIES, ...(hasOther ? ["Other"] : [])].map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCatFilter(c)}
            data-testid={`category-pill-${c}`}
            className={`px-4 h-9 rounded-full border-2 text-sm font-semibold transition-colors ${
              catFilter === c
                ? "bg-[#c9a24a] border-[#c9a24a] text-white shadow-sm"
                : "bg-white border-zinc-200 text-zinc-700 hover:border-[#c9a24a] hover:text-[#8a6a1c]"
            }`}
          >
            {c === "all" ? t("categories.all") : t(`categories.${c}`)}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500">{t("common.loading")}</p>
      ) : filtered.length === 0 ? (
        <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 text-sm text-zinc-600">
          {t("inventory.no_products")}
        </div>
      ) : (
        <div className="space-y-10">
          {grouped.map(([cat, list]) => (
            <section key={cat} data-testid={`category-section-${cat}`}>
              <div className="flex items-center gap-4 mb-5">
                <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight uppercase whitespace-nowrap">
                  {t(`categories.${cat}`)}
                </h2>
                <span className="text-sm font-semibold text-[#8a6a1c] bg-[#fbf6e7] border border-[#c9a24a] rounded-full px-2.5 py-0.5">
                  {list.length}
                </span>
                <div className="flex-1 h-[3px] bg-[#c9a24a]/70 rounded-full" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {list.map((p) => (
                  <ProductRow
                    key={p.id}
                    product={p}
                    onAdjusted={load}
                    onDelete={() => handleDelete(p.id)}
                    onEdit={() => setEditing(p)}
                    onMerge={canCreate ? () => setMerging(p) : undefined}
                    onAssign={() => setAssigning(p)}
                    onMarkSold={canManage ? () => setSelling(p) : undefined}
                    onDirectSale={canManage ? () => setDirectSaleFor(p) : undefined}
                    onProduction={() => setProducing(p)}
                    onConvert={
                      canManage && isEliteConvertible(p)
                        ? () => setConverting(p)
                        : undefined
                    }
                    readOnly={!canManage}
                    hideAssign={!canManage}
                    hideDelete={!canCreate}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {editing && (
        <EditDialog product={editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
      {merging && (
        <MergeDialog
          product={merging}
          candidates={products.filter((x) => x.id !== merging.id)}
          onClose={() => setMerging(null)}
          onMerged={load}
        />
      )}
      {assigning && (
        <AssignProductDialog product={assigning} onClose={() => setAssigning(null)} onAssigned={load} />
      )}
      {selling && (
        <AssignProductDialog
          product={selling}
          initialTransferType="sale"
          onClose={() => setSelling(null)}
          onAssigned={load}
        />
      )}
      {showSaleRequest && (
        <SaleRequestDialog isMaster onClose={() => { setShowSaleRequest(false); load(); }} />
      )}
      {showBulkGive && (
        <BulkGiveDialog products={products} initial={bulkGiveInitial}
          onClose={() => { setShowBulkGive(false); setBulkGiveInitial(null); }} onDone={load} />
      )}
      {showPartnerSale && (
        <SaleRequestDialog onClose={() => { setShowPartnerSale(false); load(); }} />
      )}
      {directSaleFor && (
        <SaleRequestDialog
          isMaster={["owner", "admin", "cfo"].includes(user?.role)}
          initialProductId={directSaleFor.id}
          onClose={() => { setDirectSaleFor(null); load(); }}
        />
      )}
      {producing && (
        <ProductionDialog product={producing} onClose={() => setProducing(null)} onDone={load} />
      )}
      {converting && (
        <ConvertToNasalDialog product={converting} onClose={() => setConverting(null)} onDone={load} />
      )}
    </div>
  );
}

// (cascade-delete dialog moved to /components/DeleteCascadeDialog.jsx — used only on TrashPage now)


export function PriceTags({ product, className = "", showPersonal = false, warnMissingRetail = false }) {
  const { t } = useTranslation();
  const { canSeeCost, user } = useAuth();
  const fmt = (n) => {
    if (n === null || n === undefined || n === "") return null;
    const num = Number(n);
    if (Number.isNaN(num)) return null;
    return `$${num.toFixed(2)}`;
  };
  const ws = fmt(product.wholesale_price);
  const rt = fmt(product.suggested_retail_price);
  const cost = canSeeCost ? fmt(product.landed_cost) : null;
  // v400.98: internal personal-use buy rate, shown in the master inventory so
  // partners know what they'd pay ($25 low-cost / $35 premium / $40 KLOW & GLOW).
  // Mirrors backend personal_use_guideline().
  let personal = null;
  if (showPersonal && ["owner", "admin", "cfo", "partner"].includes(user?.role)) {
    const n = (product.name || "").trim().toLowerCase();
    personal = n.startsWith("klow") || n.startsWith("glow")
      ? 40
      : Number(product.wholesale_price || 0) <= 40 ? 25 : 35;
  }
  // v414.22 — a master card without a retail shows as "Ask" on every store
  // and can't be ordered. Surface that loudly right on the inventory card.
  const missingRetail = warnMissingRetail && !rt;
  if (!ws && !rt && !cost && !personal && !missingRetail) return null;
  return (
    <div className={`mt-3 flex flex-wrap items-center gap-2 ${className}`} data-testid={`price-tags-${product.id}`}>
      {missingRetail && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-400 text-xs font-semibold text-amber-700"
          data-testid={`no-retail-warning-${product.id}`}>
          {t("pricing.no_retail_warning")}
        </span>
      )}
      {cost && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#fbf6e7] border border-[#c9a24a] text-xs font-medium text-[#8a6a1c]">
          <span>{t("pricing.landed_short")}</span>
          <span className="font-semibold" data-testid={`landed-cost-${product.id}`}>{cost}</span>
        </span>
      )}      {ws && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-xs font-medium text-zinc-700">
          <span className="text-zinc-500">{t("pricing.wholesale_short")}</span>
          <span className="font-semibold text-zinc-900" data-testid={`wholesale-${product.id}`}>{ws}</span>
        </span>
      )}
      {rt && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-950 border border-zinc-950 text-xs font-medium text-white">
          <span className="text-zinc-400">{t("pricing.retail_short")}</span>
          <span className="font-semibold text-white" data-testid={`retail-${product.id}`}>{rt}</span>
        </span>
      )}
      {personal != null && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#fbf6e7] border border-[#c9a24a]/60 text-xs font-medium text-[#8a6a1c]">
          <span>{t("pricing.personal_short")}</span>
          <span className="font-semibold" data-testid={`personal-price-${product.id}`}>${personal.toFixed(2)}</span>
        </span>
      )}
    </div>
  );
}

// v401.7 MEDIUM CARD: original card structure at ~half the height.
// Photo area shrunk to a small thumbnail (removed entirely when no photo),
// tightened padding/margins, full labeled action buttons in one compact row.
export function ProductRow({ product, onAdjusted, onDelete, onEdit, onMerge, onAssign, onMarkSold, onDirectSale, onProduction, onConvert, readOnly, hideAssign, hideDelete, idPrefix = "" }) {
  const { t } = useTranslation();
  const percent = product.starting_stock > 0 ? (product.current_stock / product.starting_stock) * 100 : 0;
  const low = percent < 40;
  // v405.7 Duplicate Detector — backend attaches similar_products when this
  // card's name looks like another card in the same category.
  const dupes = product.similar_products || [];
  const isDupe = dupes.length > 0;
  const [busy, setBusy] = useState(false);

  const adjust = async (delta) => {
    setBusy(true);
    try {
      await api.post(`/products/${product.id}/adjust`, { delta });
      onAdjusted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`${idPrefix}product-row-${product.id}`}
      className={`bg-white border-2 ${low ? "border-red-300" : isDupe ? "border-amber-400" : "border-black"} rounded-xl p-3 shadow-sm flex gap-3`}
    >
      {product.photo_path && (
        <img
          src={fileUrl(product.photo_path)}
          alt={product.name}
          className="w-14 h-14 border border-zinc-200 rounded-lg object-cover shrink-0"
          crossOrigin="use-credentials"
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <ProductTitle name={product.name} id={product.id} className="font-semibold text-base truncate" />
            {product.description && (
              <p className="text-xs text-zinc-600 line-clamp-1">{product.description}</p>
            )}
          </div>
          <StockBadge percent={percent} />
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="flex items-baseline gap-1.5">
            <span className="text-xl font-semibold leading-none">{product.current_stock}</span>
            <span className="text-xs text-zinc-600">
              / {product.starting_stock} {product.unit || t("common.units")}
            </span>
          </span>
          {(product.held_by_partners || 0) > 0 && (
            <span
              className="inline-flex flex-wrap items-center gap-x-1.5 text-[10px] font-medium text-[#8a6a1c] bg-[#fbf6e7] border border-[#c9a24a] rounded-full px-2 py-0.5"
              data-testid={`partner-holdings-${product.id}`}
            >
              <span className="font-semibold">{t("inventory.held_by_partners", { n: product.held_by_partners })}</span>
              <span className="text-[#c9a24a]">·</span>
              <span>{(product.partner_holdings || []).map((h) => `${h.name} ${h.qty}`).join(" · ")}</span>
            </span>
          )}
        </div>

        <div className="h-1.5 mt-1.5 bg-zinc-100 border border-zinc-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${low ? "bg-red-600" : "bg-[#c9a24a]"}`}
            style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
          />
        </div>

        <PriceTags product={product} showPersonal warnMissingRetail className="!mt-1.5" />

        {isDupe && (
          <button
            type="button"
            onClick={onMerge}
            disabled={!onMerge}
            data-testid={`dupe-flag-${product.id}`}
            className={`mt-2 inline-flex flex-wrap items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-500 text-[11px] font-semibold text-amber-800 text-left ${
              onMerge ? "hover:bg-amber-100 cursor-pointer" : "cursor-default"
            } transition-colors`}
          >
            <Warning size={13} weight="fill" className="text-amber-600 shrink-0" />
            <span>{t("inventory.dupe_flag", { name: dupes.map((s) => s.name).join(", ") })}</span>
            {onMerge && (
              <span className="inline-flex items-center gap-1 text-amber-700 underline underline-offset-2">
                <ArrowsMerge size={12} weight="bold" /> {t("inventory.dupe_merge_hint")}
              </span>
            )}
          </button>
        )}

        {!readOnly && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => adjust(-1)}
              disabled={busy || product.current_stock <= 0}
              data-testid={`decrement-${product.id}`}
              className="w-9 h-9 bg-white text-black border border-zinc-200 rounded-lg shadow-sm hover:-translate-y-px transition-all flex items-center justify-center text-xl font-semibold disabled:opacity-50"
            >
              −
            </button>
            <button
              onClick={() => adjust(1)}
              disabled={busy}
              data-testid={`increment-${product.id}`}
              className="w-9 h-9 bg-[#c9a24a] text-black border border-zinc-200 rounded-lg shadow-sm hover:-translate-y-px transition-all flex items-center justify-center text-xl font-semibold disabled:opacity-50"
            >
              +
            </button>
            <div className="flex-1" />
            {!hideAssign && (
              <BrutalButton size="sm" variant="secondary" onClick={onAssign} data-testid={`assign-${product.id}`}>
                <PaperPlaneTilt size={16} weight="bold" /> {t("inventory.give")}
              </BrutalButton>
            )}
            {onMarkSold && (
              <BrutalButton
                size="sm"
                variant="secondary"
                onClick={onMarkSold}
                data-testid={`mark-sold-${product.id}`}
                className="!border-emerald-600 !text-emerald-700 hover:!bg-emerald-50"
              >
                <CurrencyDollar size={16} weight="bold" /> {t("inventory.mark_sold")}
              </BrutalButton>
            )}
            {onDirectSale && (
              <BrutalButton
                size="sm"
                variant="secondary"
                onClick={onDirectSale}
                data-testid={`direct-sale-${product.id}`}
                className="!border-[#c9a24a] !text-[#8a6a1c] hover:!bg-[#fbf6e7]"
              >
                <CurrencyDollar size={16} weight="bold" /> {t("sale_request.direct_sale")}
              </BrutalButton>
            )}
            {onProduction && (
              <BrutalButton
                size="sm"
                variant="secondary"
                onClick={onProduction}
                data-testid={`production-${product.id}`}
                className="!border-[#c9a24a] !text-[#8a6a1c]"
              >
                <Factory size={16} weight="bold" /> {t("production.button")}
              </BrutalButton>
            )}
            {onConvert && (
              <BrutalButton
                size="sm"
                variant="secondary"
                onClick={onConvert}
                disabled={product.current_stock <= 0}
                data-testid={`convert-nasal-${product.id}`}
                className="!border-[#c9a24a] !text-[#8a6a1c]"
              >
                <SprayBottle size={16} weight="bold" /> {t("convert.button")}
              </BrutalButton>
            )}
            <BrutalButton size="sm" variant="secondary" onClick={onEdit} data-testid={`edit-${product.id}`}>
              <PencilSimple size={16} weight="bold" /> {t("common.edit")}
            </BrutalButton>
            {onMerge && (
              <BrutalButton size="sm" variant="secondary" onClick={onMerge} data-testid={`merge-${product.id}`}>
                <ArrowsMerge size={16} weight="bold" /> {t("inventory.merge_btn")}
              </BrutalButton>
            )}
            {!hideDelete && (
              <BrutalButton size="sm" variant="warning" onClick={onDelete} data-testid={`delete-${product.id}`}>
                <Trash size={16} weight="bold" />
              </BrutalButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ProductionDialog({ product, onClose, onDone }) {
  const { t } = useTranslation();
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const n = Math.max(1, Number(qty) || 0);
  const unitCost = Number(product.wholesale_price) || 0;
  const estCost = (n * unitCost).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/products/mark-production", {
        product_id: product.id,
        quantity: n,
        note: note.trim() || null,
      });
      toast.success(t("production.logged", { n, name: product.name }));
      onDone();
      onClose();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border-2 border-[#c9a24a] rounded-xl shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="production-dialog"
      >
        <div className="flex items-center gap-2 mb-1">
          <Factory size={22} weight="bold" className="text-[#8a6a1c]" />
          <h2 className="text-xl font-semibold">{t("production.dialog_title")}</h2>
        </div>
        <p className="text-sm text-zinc-600 mb-4">
          {t("production.dialog_subtitle", { name: product.name, n: product.current_stock })}
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("production.quantity")}</label>
            <BrutalInput
              type="number"
              min="1"
              max={product.current_stock}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              data-testid="production-quantity-input"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">
              {t("production.note_label")} <span className="text-zinc-400 font-normal">({t("common.optional")})</span>
            </label>
            <BrutalInput
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("production.note_placeholder")}
              data-testid="production-note-input"
            />
          </div>
          {unitCost > 0 && (
            <p className="text-sm font-semibold text-[#8a6a1c] bg-[#fbf6e7] border border-[#c9a24a] rounded-lg px-3 py-2" data-testid="production-cost-preview">
              {t("production.cost_preview", { cost: estCost })}
            </p>
          )}
          <div className="flex gap-3 pt-2">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="production-submit">
              {busy ? t("production.processing") : t("production.confirm")}
            </BrutalButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConvertToNasalDialog({ product, onClose, onDone }) {
  const { t } = useTranslation();
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const n = Math.max(1, Number(qty) || 0);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${product.id}/convert-to-nasal`, { quantity: n });
      toast.success(t("convert.done", { n, name: data.nasal_product?.name || product.name }));
      onDone();
      onClose();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border-2 border-[#c9a24a] rounded-xl shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="convert-nasal-dialog"
      >
        <div className="flex items-center gap-2 mb-1">
          <SprayBottle size={22} weight="bold" className="text-[#8a6a1c]" />
          <h2 className="text-xl font-semibold">{t("convert.dialog_title")}</h2>
        </div>
        <p className="text-sm text-zinc-600 mb-4">
          {t("convert.dialog_subtitle", { name: product.name, n: product.current_stock })}
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("convert.quantity")}</label>
            <BrutalInput
              type="number"
              min="1"
              max={product.current_stock}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              data-testid="convert-quantity-input"
            />
          </div>
          <p className="text-sm font-medium text-[#8a6a1c] bg-[#fbf6e7] border border-[#c9a24a] rounded-lg px-3 py-2">
            {t("convert.preview", { n, name: `${product.name} (Nasal)` })}
          </p>
          <div className="flex gap-3 pt-2">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="convert-submit">
              {busy ? t("convert.processing") : t("convert.confirm")}
            </BrutalButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditDialog({ product, onClose, onSaved }) {
  const { t } = useTranslation();
  const { canSeeCost } = useAuth();
  const [form, setForm] = useState({
    name: product.name,
    description: product.description || "",
    starting_stock: product.starting_stock,
    current_stock: product.current_stock,
    unit: product.unit || "units",
    wholesale_price: product.wholesale_price ?? "",
    suggested_retail_price: product.suggested_retail_price ?? "",
    landed_cost: product.landed_cost ?? "",
    category: product.category || "Injectable",
    coming_soon: !!product.coming_soon,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch(`/products/${product.id}`, {
        name: form.name,
        description: form.description,
        starting_stock: Number(form.starting_stock),
        current_stock: Number(form.current_stock),
        unit: form.unit,
        wholesale_price: form.wholesale_price === "" ? null : Number(form.wholesale_price),
        suggested_retail_price: form.suggested_retail_price === "" ? null : Number(form.suggested_retail_price),
        category: form.category,
        coming_soon: !!form.coming_soon,
        // Never sent by non-allowlisted users — their form value is blank
        // (scrubbed server-side) and would wipe the real cost.
        ...(canSeeCost
          ? { landed_cost: form.landed_cost === "" ? null : Number(form.landed_cost) }
          : {}),
      });
      // v402.5 Catalog Editor: report how many distributor rows were repriced.
      const sweptCount = data?.price_sweep
        ? Object.values(data.price_sweep).reduce((a, b) => a + b, 0)
        : 0;
      if (sweptCount > 0) {
        toast.success(t("inventory.updated_with_sweep", { count: sweptCount }));
      } else {
        toast.success(t("inventory.updated"));
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-product-dialog"
      >
        <h2 className="text-2xl font-semibold mb-4">{t("inventory.edit_product")}</h2>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1">{t("common.name")}</label>
            <BrutalInput
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("inventory.description")}</label>
            <BrutalInput
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">{t("inventory.starting")}</label>
              <BrutalInput
                type="number"
                min="0"
                value={form.starting_stock}
                onChange={(e) => setForm({ ...form, starting_stock: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t("inventory.current")}</label>
              <BrutalInput
                type="number"
                min="0"
                value={form.current_stock}
                onChange={(e) => setForm({ ...form, current_stock: e.target.value })}
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("inventory.unit")}</label>
            <BrutalInput
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1">{t("categories.label")}</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="edit-category-select"
              className="w-full h-11 px-3 bg-white border border-zinc-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#c9a24a]"
            >
              {PRODUCT_CATEGORIES.map((c) => (
                <option key={c} value={c}>{t(`categories.${c}`)}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer select-none" data-testid="edit-coming-soon-row">
            <input
              type="checkbox"
              checked={form.coming_soon}
              onChange={(e) => setForm({ ...form, coming_soon: e.target.checked })}
              data-testid="edit-coming-soon-checkbox"
              className="w-4 h-4 accent-[#c9a24a]"
            />
            {t("inventory.coming_soon_label")}
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1">{t("pricing.wholesale_price")}</label>
              <BrutalInput
                data-testid="edit-wholesale-input"
                type="number"
                step="0.01"
                min="0"
                value={form.wholesale_price}
                onChange={(e) => setForm({ ...form, wholesale_price: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">{t("pricing.suggested_retail")}</label>
              <BrutalInput
                data-testid="edit-retail-input"
                type="number"
                step="0.01"
                min="0"
                value={form.suggested_retail_price}
                onChange={(e) => setForm({ ...form, suggested_retail_price: e.target.value })}
                placeholder="0.00"
              />
            </div>
          </div>
          <p className="text-[11px] text-zinc-500 -mt-1" data-testid="edit-price-sweep-hint">
            {t("pricing.master_sweep_hint")}
          </p>
          {canSeeCost && (
            <div>
              <label className="block text-xs font-semibold mb-1 text-[#8a6a1c]">{t("pricing.landed_cost")}</label>
              <BrutalInput
                data-testid="edit-landed-cost-input"
                type="number"
                step="0.01"
                min="0"
                value={form.landed_cost}
                onChange={(e) => setForm({ ...form, landed_cost: e.target.value })}
                placeholder="0.00"
                className="!border-[#c9a24a]"
              />
              <p className="text-[11px] text-zinc-500 mt-1">{t("pricing.landed_cost_hint")}</p>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={onClose}>
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton type="submit" className="flex-1" disabled={busy} data-testid="edit-product-save">
              {busy ? t("common.saving") : t("common.save")}
            </BrutalButton>
          </div>
        </form>
      </div>
    </div>
  );
}

// v405.6 — merge a duplicate card (e.g. scan-created 'BBG70') into the real
// catalog card ('GLOW70 (BBG70)'). Stock + full history move to the target.
function MergeDialog({ product, candidates, onClose, onMerged }) {
  const { t } = useTranslation();
  const [targetId, setTargetId] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const target = candidates.find((c) => c.id === targetId);
  // v405.7 — suspected duplicates (flagged by the backend detector) float to
  // the top of the candidate list with a "likely match" tag.
  const similarIds = new Set((product.similar_products || []).map((s) => s.id));
  const shown = candidates
    .filter((c) => !q.trim() || c.name.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => (similarIds.has(b.id) ? 1 : 0) - (similarIds.has(a.id) ? 1 : 0));

  const submit = async () => {
    if (!targetId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${product.id}/merge/${targetId}`);
      toast.success(t("inventory.merged_ok", { name: data.target?.name || "" }));
      onMerged();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="merge-dialog"
      >
        <h2 className="text-2xl font-semibold mb-1">{t("inventory.merge_title")}</h2>
        <p className="text-sm text-zinc-600 mb-4">
          {t("inventory.merge_into_label", { name: product.name })}
        </p>
        <BrutalInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("inventory.merge_search")}
          data-testid="merge-search"
        />
        <div className="mt-2 max-h-56 overflow-y-auto border border-zinc-200 rounded-lg divide-y divide-zinc-100" data-testid="merge-candidates">
          {shown.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setTargetId(c.id)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${
                targetId === c.id ? "bg-[#fbf6e7] font-semibold" : "hover:bg-zinc-50"
              }`}
              data-testid={`merge-candidate-${c.id}`}
            >
              <span className="min-w-0 truncate flex items-center gap-1.5">
                <span className="truncate">{c.name}</span>
                {similarIds.has(c.id) && (
                  <span
                    className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-px rounded-full bg-amber-50 border border-amber-500 text-[10px] font-semibold text-amber-700"
                    data-testid={`likely-match-${c.id}`}
                  >
                    <Warning size={10} weight="fill" /> {t("inventory.dupe_likely")}
                  </span>
                )}
              </span>
              <span className="text-xs text-zinc-500 tabular-nums shrink-0">
                {c.current_stock} {c.unit || ""}
              </span>
            </button>
          ))}
          {shown.length === 0 && (
            <p className="px-3 py-3 text-sm text-zinc-500">{t("inventory.merge_no_match")}</p>
          )}
        </div>
        {target && (
          <p className="mt-3 text-sm font-semibold text-[#8a6a1c]" data-testid="merge-preview">
            {t("inventory.merge_preview", {
              a: product.current_stock,
              b: target.current_stock,
              total: (product.current_stock || 0) + (target.current_stock || 0),
              name: target.name,
            })}
          </p>
        )}
        <p className="mt-2 text-xs text-red-700">{t("inventory.merge_warning")}</p>
        <div className="flex gap-2 justify-end pt-4">
          <BrutalButton variant="secondary" onClick={onClose} data-testid="merge-cancel">
            {t("common.cancel")}
          </BrutalButton>
          <BrutalButton onClick={submit} disabled={busy || !targetId} data-testid="merge-confirm">
            {busy ? t("common.saving") : t("inventory.merge_confirm")}
          </BrutalButton>
        </div>
      </div>
    </div>
  );
}
